import {Trans} from '@lingui/macro';
import {DateTime} from 'luxon';
import {ellipsis} from 'polished';
import React, {FC, ReactNode, useCallback, useMemo, useState} from 'react';
import styled from 'styled-components';

import {HandlerOf} from '../../../libs/shared/helpers/src/react/reactHelpers';
import {Toggle} from '../../../libs/shared/ui/src/components/booleanInputs/toggle';
import {Icon} from '../../../libs/shared/ui/src/components/icon/icon';
import {VisualSizesEnum} from '../../../libs/shared/ui/src/styles/commonStyles';
import {fontSizes, fontWeights} from '../../../libs/shared/ui/src/styles/fontStyles';
import {center} from '../../../libs/shared/ui/src/styles/mixins';
import {PaletteColorsEnum} from '../../../libs/shared/ui/src/styles/paletteModel';
import {boxShadow} from '../../../libs/shared/ui/src/theme/themeStyleHelpers';
import {useUserTheme} from '../../../libs/shared/ui/src/theme/userThemeProvider';
import {useDoNotDisturb} from '../../../srcCommon/app/doNotDisturbHooks';
import {useLocalize} from '../../../srcCommon/internationalization/internationalizationContext';
import {findTeammateAvailabilityColor} from '../../components/app/topBar/userProfile/userProfileHelpers';
import {AvatarBadgeStatus} from '../../components/common/avatars/avatarBadgeStatus';
import {TeammateAvatar} from '../../components/common/avatars/teammateAvatar';
import {TimeComboBox} from '../../components/common/dateTime/timeComboBox';
import {errorFallbackMessage} from '../../components/errors/errorFallbackMessage';
import {formatTime, TimeFormatsEnum} from '../../helpers/dateTimeHelpers';
import {buildTeammateDisplayName} from '../../helpers/modelHelpers';
import {DoNotDisturb} from '../../models/api/preferencesModel';
import {TeammateModel} from '../../models/api/teammateModel';
import {
  AvailabilityStatus,
  AvailabilityStatusTypesEnum,
} from '../../store/data/teammates/availabilityStatusSelectors';
import {Localizer} from '../../store/stateless/internationalizationSelectors';
import {SettingsUserAvatarDialog} from '../profile/settingsUserAvatarDialog';
import {ProfileTogglesEnum, ProfileTogglesState} from './settingsHomeTypes';

/*
 * Props.
 */

interface SettingsHomeProfileProps {
  onProfileToggleChange: HandlerOf<ProfileTogglesState>;
  onAvatarChange: HandlerOf<File>;
  doNotDisturb: DoNotDisturb;
  shouldMuteNotifications: boolean;
  user: TeammateModel;
  userTimeFormat: TimeFormatsEnum;
  userAvailabilityStatus: AvailabilityStatus;
}

/*
 * Style.
 */

const SettingsUserHomeProfileDiv = styled.div`
  grid-area: profile;
  flex: 1;
  border-radius: 16px;
  background: ${(p) => p.theme.greys.white};
  ${(p) => boxShadow(p.theme, `0 1px 9px ${p.theme.alphas.black20}`)};
  border: 1px solid ${(p) => p.theme.alphas.black20};

  display: grid;
  grid-template-areas:
    'status'
    '.'
    'ooo'
    '.'
    'dnd';
  grid-template-rows: auto 19px 82px 10px auto;
  padding: 19px;
`;

const SettingsHomeProfileBlockGridDiv = styled.div`
  display: grid;
  grid-template-areas:
    'icon . title . toggle'
    'icon . content . toggle';
  grid-template-columns: 30px 12px 1fr minmax(10px, 45px) 30px;
  grid-template-rows: repeat(2, auto);
  padding: 15px;
  padding-top: 15px;
  padding-bottom: 12px;

  & + & {
    border-top: 1px solid ${(p) => p.theme.alphas.black20};
    padding-bottom: 15px;
  }
`;

const SettingsHomeProfileBlockDiv = styled.div`
  background: ${(p) => p.theme.greys.white};
  border-radius: 12px;
  border: 1px solid ${(p) => p.theme.greys.shade30};
`;

const SettingsHomeProfileBlockIconWrapperDiv = styled.div`
  grid-area: icon;
`;

type SettingsHomeProfileIconStyleProps = {
  $color: string;
};
const SettingsHomeProfileIcon = styled.div<SettingsHomeProfileIconStyleProps>`
  border-radius: 50%;
  height: 30px;
  width: 30px;
  ${center()};
  background-color: ${(p) => p.$color};
  color: ${(p) => p.theme.greys.white};

  svg {
    width: 16px;
    height: 16px;
  }
`;

const SettingsHomeProfileBlockTitleDiv = styled.div`
  grid-area: title;
  font-size: ${fontSizes.medium};
  line-height: ${fontSizes.medium};
  font-weight: ${fontWeights.semibold};
  color: ${(p) => p.theme.greys.shade80};
`;
const SettingsHomeProfileBlockContentWrapperDiv = styled.div`
  grid-area: content;
  margin-top: 3px;
`;

const SettingsHomeProfileBlockContentDiv = styled.div`
  font-size: ${fontSizes.verySmall};
  line-height: 18px;
  font-weight: ${fontWeights.normal};
  color: ${(p) => p.theme.greys.shade80};
`;

const SettingsHomeProfileScheduleBlock = styled.div`
  width: 180px;
  display: grid;
  grid-template-columns: 77px auto 77px;
  grid-auto-rows: auto;
`;

const SettingsHomeProfileScheduleBlockSeparator = styled.span`
  color: ${(p) => p.theme.greys.shade70};
  font-weight: ${fontWeights.semibold};
  font-size: ${fontSizes.small};
  ${center()};
  margin-top: 6px;
`;

const SettingsHomeProfileBlockToggleDiv = styled.div`
  grid-area: toggle;
  ${center()};

  > label {
    margin-top: -4px;
  }
`;

const ScheduleToggleDiv = styled(SettingsHomeProfileBlockToggleDiv)`
  justify-content: initial;
  align-items: initial;

  > label {
    margin-top: 4px;
  }
`;

const SettingsHomeProfileOoo = styled(SettingsHomeProfileBlockDiv)`
  grid-area: ooo;
`;

const SettingsHomeProfileDnd = styled(SettingsHomeProfileBlockDiv)`
  grid-area: dnd;
`;

const SettingsHomeProfileStatusDiv = styled.div`
  overflow: hidden;

  height: 70px;
  display: grid;
  grid-template-columns: 70px 1fr;
  grid-template-rows: repeat(3, auto);
  grid-template-areas:
    'avatar name'
    'avatar username'
    'avatar status';
  grid-column-gap: 17px;
  grid-row-gap: 0;
`;

const SettingsHomeProfileAvatarDiv = styled.div`
  grid-area: avatar;
  height: 70px;
  position: relative;
`;

const SettingsHomeProfileAvatarOverlaySpan = styled.span`
  height: 70px;
  width: 70px;
  position: absolute;
  border-radius: 50%;
  top: 0;
  background: rgba(0, 0, 0, 0.6);
  z-index: 1;
  color: ${(p) => p.theme.greys.white};
  ${center()};
  font-weight: ${fontWeights.medium};
  font-size: ${fontSizes.medium};
  opacity: 0;
  pointer-events: none;

  ${SettingsHomeProfileAvatarDiv}:hover & {
    opacity: 1;
  }
`;

const SettingsHomeProfileName = styled.div`
  grid-area: name;
  font-size: ${fontSizes.large};
  line-height: 19px;
  font-weight: ${fontWeights.bold};
  color: ${(p) => p.theme.greys.shade80};
  margin-top: 4px;
  ${ellipsis()};
`;

const SettingsHomeProfileUsername = styled.div`
  grid-area: username;
  font-size: ${fontSizes.verySmall};
  line-height: 20px;
  font-weight: ${fontWeights.normal};
  color: ${(p) => p.theme.greys.shade80};
  margin-top: -3px;
  ${ellipsis()};
`;

const SettingsHomeProfileStatusWrapper = styled.div`
  grid-area: status;
`;

type SettingsHomeProfileStatusStyleProps = {
  $color: PaletteColorsEnum;
};
const SettingsHomeProfileStatus = styled.div<SettingsHomeProfileStatusStyleProps>`
  display: inline-block;
  font-size: ${fontSizes.verySmall};
  line-height: 14px;
  font-weight: ${fontWeights.bold};

  padding: 3px 6px;
  background: ${(p) => p.theme.palette[p.$color].shade20};
  color: ${(p) => p.theme.palette[p.$color].shade40};
  border-radius: 106px;
`;

const StyledAvatarBadgeStatus = styled(AvatarBadgeStatus)<{$isDialogOpen: boolean}>`
  width: 14px;
  height: 14px;
  ${(p) => boxShadow(p.theme, `0 0 0 3px ${p.theme.greys.white}`)};
  z-index: ${(p) => (p.$isDialogOpen ? 0 : 1)};
  right: 11%;
`;

const StyledTimeComboBox = styled(TimeComboBox)`
  margin: 0;
  margin-top: 8px;
`;

/*
 * Component.
 */

export const SettingsHomeProfile: FC<SettingsHomeProfileProps> = (props) => {
  const theme = useUserTheme();
  const {onProfileToggleChange} = props;
  const paletteColorForAvailability = findTeammateAvailabilityColor(props.userAvailabilityStatus.type);
  const badgeColor = theme.palette[paletteColorForAvailability].shade40;

  const togglesState = useMemo(
    () => ({
      [ProfileTogglesEnum.OUT_OF_OFFICE]: !props.user.isAvailable,
      [ProfileTogglesEnum.MUTE]: props.shouldMuteNotifications,
      [ProfileTogglesEnum.DO_NOT_DISTURB]: props.doNotDisturb.isEnabled,
    }),
    [props.doNotDisturb.isEnabled, props.shouldMuteNotifications, props.user.isAvailable],
  );

  const onToggleStateChange = useCallback(
    (key: ProfileTogglesEnum, newValue: boolean) => {
      const isTurningOn = newValue === true;

      // If the user is turning Out of office on AND Mute is on, then we want to turn Out of office on and Mute off.
      if (key === ProfileTogglesEnum.OUT_OF_OFFICE && isTurningOn && togglesState.MUTE === true) {
        onProfileToggleChange({
          ...togglesState,
          [ProfileTogglesEnum.OUT_OF_OFFICE]: true,
          [ProfileTogglesEnum.MUTE]: false,
        });
      }
      // If the user is turning Mute on AND Out of office is on then we want to turn Mute on and Out of office off.
      else if (key === ProfileTogglesEnum.MUTE && isTurningOn && togglesState.OUT_OF_OFFICE === true) {
        onProfileToggleChange({
          ...togglesState,
          [ProfileTogglesEnum.OUT_OF_OFFICE]: false,
          [ProfileTogglesEnum.MUTE]: true,
        });
      }
      // Otherwise, we can update normally.
      else {
        onProfileToggleChange({
          ...togglesState,
          [key]: newValue,
        });
      }
    },
    [onProfileToggleChange, togglesState],
  );

  const {interval, onStartTimeChange, onEndTimeChange} = useDoNotDisturb();
  const localizer = useLocalize();
  const [isAvatarDialogOpen, setIsAvatarDialogOpen] = useState(false);
  const maybeRenderDialogs = () => {
    if (isAvatarDialogOpen) {
      return (
        <SettingsUserAvatarDialog
          onRequestClose={() => {
            setIsAvatarDialogOpen(false);
          }}
        />
      );
    }

    return null;
  };

  return (
    <>
      {maybeRenderDialogs()}
      <SettingsUserHomeProfileDiv>
        <SettingsHomeProfileStatusDiv>
          <SettingsHomeProfileAvatarDiv
            onClick={() => {
              setIsAvatarDialogOpen(true);
            }}
          >
            <SettingsHomeProfileAvatarOverlaySpan>
              <Trans id="settingshomeprofile-edit">Edit</Trans>
            </SettingsHomeProfileAvatarOverlaySpan>
            <TeammateAvatar
              size={70}
              teammate={props.user}
              key={props.user.avatarUrl}
              borderColor="white"
              borderHoverColor="white"
            >
              <StyledAvatarBadgeStatus
                teammate={props.user}
                size={VisualSizesEnum.LARGE}
                color={badgeColor}
                $isDialogOpen={isAvatarDialogOpen}
              />
            </TeammateAvatar>
          </SettingsHomeProfileAvatarDiv>
          <SettingsHomeProfileName>
            {buildTeammateDisplayName(props.user) || props.user.email}
          </SettingsHomeProfileName>
          <SettingsHomeProfileUsername>@{props.user.alias}</SettingsHomeProfileUsername>
          <SettingsHomeProfileStatusWrapper>
            <SettingsHomeProfileStatus $color={paletteColorForAvailability}>
              {renderAvailabilityStatus(props, localizer)}
            </SettingsHomeProfileStatus>
          </SettingsHomeProfileStatusWrapper>
        </SettingsHomeProfileStatusDiv>
        <SettingsHomeProfileOoo>
          <SettingsHomeProfileBlockGridDiv>
            <SettingsHomeProfileBlockIconWrapperDiv>
              <SettingsHomeProfileIcon $color={theme.palette.red.shade40}>
                <Icon name="bellCrossedSmall" />
              </SettingsHomeProfileIcon>
            </SettingsHomeProfileBlockIconWrapperDiv>
            <SettingsHomeProfileBlockTitleDiv>
              <Trans id="settingshome-ooo">Out of office</Trans>
            </SettingsHomeProfileBlockTitleDiv>
            <SettingsHomeProfileBlockContentWrapperDiv>
              <SettingsHomeProfileBlockContentDiv>
                <Trans id="settingshomeprofile-muteunassign">
                  Mute notifications and unassign new messages
                </Trans>
              </SettingsHomeProfileBlockContentDiv>
            </SettingsHomeProfileBlockContentWrapperDiv>
            <SettingsHomeProfileBlockToggleDiv>
              <Toggle
                onChange={(val) => onToggleStateChange(ProfileTogglesEnum.OUT_OF_OFFICE, val)}
                isChecked={togglesState.OUT_OF_OFFICE}
                size={VisualSizesEnum.SMALL}
              />
            </SettingsHomeProfileBlockToggleDiv>
          </SettingsHomeProfileBlockGridDiv>
        </SettingsHomeProfileOoo>
        <SettingsHomeProfileDnd>
          <SettingsHomeProfileBlockGridDiv>
            <SettingsHomeProfileBlockIconWrapperDiv>
              <SettingsHomeProfileIcon $color={theme.palette.orange.shade40}>
                <Icon name="moonFilledSmall" />
              </SettingsHomeProfileIcon>
            </SettingsHomeProfileBlockIconWrapperDiv>
            <SettingsHomeProfileBlockTitleDiv>
              <Trans id="settingshomeprofile-donotdisturb">Do not disturb</Trans>
            </SettingsHomeProfileBlockTitleDiv>
            <SettingsHomeProfileBlockContentWrapperDiv>
              <SettingsHomeProfileBlockContentDiv>
                <Trans id="settingshomeprofile-mute">Mute notifications</Trans>
              </SettingsHomeProfileBlockContentDiv>
            </SettingsHomeProfileBlockContentWrapperDiv>
            <SettingsHomeProfileBlockToggleDiv>
              <Toggle
                onChange={(val) => onToggleStateChange(ProfileTogglesEnum.MUTE, val)}
                isChecked={togglesState.MUTE}
                size={VisualSizesEnum.SMALL}
              />
            </SettingsHomeProfileBlockToggleDiv>
          </SettingsHomeProfileBlockGridDiv>
          <SettingsHomeProfileBlockGridDiv>
            <SettingsHomeProfileBlockIconWrapperDiv>
              <SettingsHomeProfileIcon $color={theme.palette.orange.shade40}>
                <Icon name="donotdisturbSmall" />
              </SettingsHomeProfileIcon>
            </SettingsHomeProfileBlockIconWrapperDiv>
            <SettingsHomeProfileBlockTitleDiv>
              <Trans id="settingshomeprofile-schedule">Schedule</Trans>
            </SettingsHomeProfileBlockTitleDiv>
            <SettingsHomeProfileBlockContentWrapperDiv>
              {interval ? (
                <SettingsHomeProfileScheduleBlock>
                  <StyledTimeComboBox
                    timeFormat={props.userTimeFormat}
                    selectedTime={interval.start}
                    onTimeSelect={(time) => time && onStartTimeChange(time)}
                  />
                  <SettingsHomeProfileScheduleBlockSeparator>
                    <Trans id="settingshomeprofile-to">to</Trans>
                  </SettingsHomeProfileScheduleBlockSeparator>
                  <StyledTimeComboBox
                    timeFormat={props.userTimeFormat}
                    selectedTime={interval.end}
                    onTimeSelect={(time) => time && onEndTimeChange(time)}
                  />
                </SettingsHomeProfileScheduleBlock>
              ) : (
                errorFallbackMessage
              )}
            </SettingsHomeProfileBlockContentWrapperDiv>
            <ScheduleToggleDiv>
              <Toggle
                onChange={(val) => onToggleStateChange(ProfileTogglesEnum.DO_NOT_DISTURB, val)}
                isChecked={togglesState.DO_NOT_DISTURB}
                size={VisualSizesEnum.SMALL}
              />
            </ScheduleToggleDiv>
          </SettingsHomeProfileBlockGridDiv>
        </SettingsHomeProfileDnd>
      </SettingsUserHomeProfileDiv>
    </>
  );
};

function renderAvailabilityStatusSuffix(props: SettingsHomeProfileProps, localizer: Localizer): ReactNode {
  if (!props.doNotDisturb.isEnabled || !props.userAvailabilityStatus.until) {
    return null;
  }

  const renderTime = (time: DateTime) => {
    const formattedTime = formatTime(localizer, props.userTimeFormat, time);

    if (props.userTimeFormat === TimeFormatsEnum.H12) {
      return formattedTime.replace(':00', '');
    }

    return formattedTime;
  };

  return (
    <>
      {' '}
      <Trans id="settingshome-until">until {renderTime(props.userAvailabilityStatus.until)}</Trans>
    </>
  );
}

function renderAvailabilityStatus(props: SettingsHomeProfileProps, localizer: Localizer) {
  const suffix = renderAvailabilityStatusSuffix(props, localizer);
  switch (props.userAvailabilityStatus.type) {
    case AvailabilityStatusTypesEnum.DO_NOT_DISTURB:
    case AvailabilityStatusTypesEnum.MUTED:
      return <Trans id="settingshome-dnd">Do not disturb{suffix}</Trans>;
    case AvailabilityStatusTypesEnum.OUT_OF_OFFICE:
      return <Trans id="settingshome-ooo">Out of office</Trans>;
    default:
      return <Trans id="settingshome-available">Available{suffix}</Trans>;
  }
}
